import { useEffect, useMemo, useState } from "react";
import { Search, Globe2, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, Loader } from "@/components/public/Bits";
import { StatusBadge } from "@/components/StatusBadge";
import { Reveal } from "@/components/Reveal";

const STATUSES = ["Active", "Pending", "Suspended", "Inactive"];

export default function Federations() {
  const [feds, setFeds] = useState(null);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    api.get("/public/federations").then((r) => setFeds(r.data)).catch(() => setFeds([]));
  }, []);

  const countries = useMemo(() => {
    if (!feds) return [];
    return Array.from(new Set(feds.map((f) => f.country).filter(Boolean))).sort();
  }, [feds]);

  const filtered = useMemo(() => {
    if (!feds) return [];
    return feds.filter((f) => {
      const matchQ = !q || f.name.toLowerCase().includes(q.toLowerCase()) || (f.country || "").toLowerCase().includes(q.toLowerCase());
      const matchC = !country || f.country === country;
      const matchS = !status || f.status === status;
      return matchQ && matchC && matchS;
    });
  }, [feds, q, country, status]);

  const hasFilters = q || country || status;

  return (
    <div>
      <PageHeader eyebrow="Directory" title="Affiliated Federations" subtitle="Browse and search affiliated federations." kanji="連盟" />
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {feds === null ? (
          <Loader />
        ) : feds.length === 0 ? (
          <EmptyState icon={Globe2} kanji="連盟" title="No federations listed yet" message="Affiliated federations will appear here once added." testid="federations-empty" />
        ) : (
          <>
            <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="relative sm:col-span-2 lg:col-span-2">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or country…"
                  data-testid="federations-search-input"
                  className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm outline-none transition focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20"
                />
              </div>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                data-testid="federations-country-filter"
                className="rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20"
              >
                <option value="">All countries</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="federations-status-filter"
                className="rounded-lg border border-slate-300 bg-white py-2.5 px-3 text-sm outline-none focus:border-ukf-red focus:ring-2 focus:ring-ukf-red/20"
              >
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="mb-5 flex items-center justify-between">
              <p className="text-sm text-slate-500" data-testid="federations-count">{filtered.length} federation{filtered.length !== 1 ? "s" : ""}</p>
              {hasFilters && (
                <button onClick={() => { setQ(""); setCountry(""); setStatus(""); }} data-testid="federations-clear-filters"
                  className="inline-flex items-center gap-1 text-sm font-medium text-ukf-red hover:underline">
                  <X className="h-3.5 w-3.5" /> Clear filters
                </button>
              )}
            </div>

            {filtered.length === 0 ? (
              <EmptyState icon={Search} title="No matches found" message="Try adjusting your search or filters." testid="federations-no-results" />
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((f, i) => (
                  <Reveal key={f.id} delay={Math.min(i * 0.03, 0.3)}>
                    <div data-testid={`federation-card-${f.id}`}
                      className="flex h-full items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-ukf-gold hover:shadow-md">
                      <div className="min-w-0">
                        <h3 className="font-serif text-lg font-semibold text-ukf-ink">{f.name}</h3>
                        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                          <Globe2 className="h-3.5 w-3.5" /> {f.country}
                        </p>
                      </div>
                      <StatusBadge status={f.status} />
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
